import { useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'

import { isComponentOn } from '@aytm/app/stats/page/question-views/components/lib/utils'

import { isClickable, isTraitType, resolvePercentDecimals } from '@aytm/app/stats/page/components/lib/utils'

import { calcSortOrder } from '@aytm/app/stats/page/question-views/oe/lib/table-utils'

import useEcommerceState from '@aytm/app/stats/page/question-views/ec/states/useEcommerceState'

// components
import BlockWrapper from '@aytm/app/stats/page/question-views/components/BlockWrapper'
import EchoScope from '@aytm/app/stats/page/question-views/components/EchoScope'
import ShelfBlock from '@aytm/app/stats/page/question-views/ec/components/shelf/ShelfBlock'
import ChartBlock from '@aytm/app/stats/page/question-views/ec/components/chart/ChartBlock'

// An ecommerce question is two stacked halves: the shelf summary ("Shop Metrics") and the
// per-product chart with its metrics table. Each collapses independently and each persists
// its own report params, but they share one model — and now one React root.
//
// `format` is the question format; `chartFormat` is its `chart` sub-object. The shelf table
// reads the former (hideTotal), the chart reads the latter, so both are passed through.
const Block = ({
  id,
  components,
  answer,
  format,
  chartFormat,
  priceTemplate,
  isAnimated,
  // report params
  params,
  paramsRevision,
  question,
  questionChartMode,
  globalChartMode,
  globalZoomLevel,
  defaultSortOrder,
  reportPercentDecimals,
  viewClickable,
  confidenceLevels,
  ignoreAmount,
  ignorePercents,
  labelTarget,
  onReady,
  onStateChange,
  // shelf
  shelfPreviewData,
  // chart
  showPrice,
  isAmazon,
  hasBannerProducts,
  hasSubscribeProducts,
  onLabelClick,
  ...chartProps
}) => {
  const chartApiRef = useRef(null)

  const {
    viewMode,
    chartCollapsed,
    shelfCollapsed,
    chartMode,
    zoomLevel,
    sortOrder,
    onModelChange,
    onShelfCollapsedChange,
    onChartCollapsedChange,
    onChartModeChange,
    onSortChange,
    onZoomChange
  } = useEcommerceState({
    params,
    paramsRevision,
    format,
    question,
    questionChartMode,
    globalChartMode,
    globalZoomLevel,
    answerVariantsCount: chartProps.answerVariants?.length || 0,
    defaultSortOrder,
    answer,
    ignoreAmount,
    ignorePercents,
    confidenceLevels,
    labelTarget,
    chartApiRef,
    onReady,
    onStateChange
  })

  // a clicked column names the order it wants; the state is what settles on one, and the shell
  // writes it down with the rest of the block's params
  const onSort = useCallback(
    (colIndex, dir, cols) => {
      const order = calcSortOrder(cols, colIndex, dir)
      if (order) onSortChange(order)
    },
    [onSortChange]
  )

  const percentDecimals = resolvePercentDecimals(format, reportPercentDecimals)
  const traitType = isTraitType({ ...question, format })

  return (
    <BlockWrapper answer={answer}>
      {isComponentOn(components, 'shelfBlock') && (
        <EchoScope name="shelf">
          <ShelfBlock
            id={id}
            answer={answer}
            viewMode={viewMode}
            format={format}
            components={components}
            priceTemplate={priceTemplate}
            shelfPreviewData={shelfPreviewData}
            shelfCollapsed={shelfCollapsed}
            onShelfCollapsedChange={onShelfCollapsedChange}
          />
        </EchoScope>
      )}
      {isComponentOn(components, 'chartBlock') && (
        <EchoScope name="chart">
          <ChartBlock
            id={id}
            answer={answer}
            viewMode={viewMode}
            format={chartFormat}
            components={components}
            priceTemplate={priceTemplate}
            isAnimated={isAnimated}
            chartCollapsed={chartCollapsed}
            showPrice={showPrice}
            isAmazon={isAmazon}
            hasBannerProducts={hasBannerProducts}
            hasSubscribeProducts={hasSubscribeProducts}
            percentDecimals={percentDecimals}
            ignoreTraits={true === format?.ignoreTraits}
            clickable={isClickable(format, viewClickable)}
            isTraitType={traitType}
            ignorePercents={ignorePercents}
            confidenceLevels={confidenceLevels}
            onChartCollapsedChange={onChartCollapsedChange}
            onLabelClick={onLabelClick}
            onModelChange={onModelChange}
            onSort={onSort}
            sortOrder={sortOrder}
            chartMode={chartMode}
            questionChartMode={questionChartMode}
            globalChartMode={globalChartMode}
            zoomLevel={zoomLevel}
            globalZoomLevel={globalZoomLevel}
            onChartModeChange={onChartModeChange}
            onZoomChange={onZoomChange}
            {...chartProps}
            onChartReady={api => {
              chartApiRef.current = api
            }}
          />
        </EchoScope>
      )}
    </BlockWrapper>
  )
}

Block.displayName = 'EcommerceBlock'

Block.propTypes = {
  id: PropTypes.string,
  components: PropTypes.object,
  answer: answerPropType,
  format: PropTypes.object,
  chartFormat: PropTypes.object,
  priceTemplate: PropTypes.string,
  isAnimated: PropTypes.bool,
  paramsRevision: PropTypes.number,
  params: PropTypes.object,
  question: PropTypes.object,
  questionChartMode: PropTypes.string,
  globalChartMode: PropTypes.string,
  globalZoomLevel: PropTypes.string,
  defaultSortOrder: PropTypes.string,
  reportPercentDecimals: PropTypes.number,
  viewClickable: PropTypes.bool,
  confidenceLevels: PropTypes.array,
  ignoreAmount: PropTypes.bool,
  ignorePercents: PropTypes.bool,
  labelTarget: PropTypes.object,
  onReady: PropTypes.func,
  onStateChange: PropTypes.func,
  shelfPreviewData: PropTypes.object,
  showPrice: PropTypes.bool,
  isAmazon: PropTypes.bool,
  hasBannerProducts: PropTypes.bool,
  hasSubscribeProducts: PropTypes.bool,
  onLabelClick: PropTypes.func
}

export default Block
